import React, { useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMatch } from '../context/MatchContext'
import { motion } from 'framer-motion'
import { recalculateMatch, applyBall } from '../utils/matchUtils'

function formatOvers(balls) {
    const overs = Math.floor(balls / 6)
    const rem = balls % 6
    return `${overs}.${rem}`
}

function getCurrentOverBalls(history, currentBalls) {
    const overNum = Math.floor(currentBalls / 6) + 1
    const overBalls = history.filter(h => h.over === overNum)
    return overBalls.map(h => {
        if (h.extra) {
            if (h.extraType === 'wide') return 'wd'
            if (h.extraType === 'noball') return 'nb'
            if (h.extraType === 'bye') return `${h.runs}b`
            if (h.extraType === 'legbye') return `${h.runs}lb`
        }
        if (h.wicket) return 'W'
        return h.runs?.toString() ?? '0'
    })
}


export default function LiveScore() {
    const navigate = useNavigate()
    const { state, updateMatch } = useMatch()
    const match = state.match
    const [showExtras, setShowExtras] = useState(false)

    useEffect(() => {
        if (state.match?.finished) {
            navigate('/summary') // auto-move to summary screen
        }
    }, [state.match?.finished])

    if (!match) return (
        <div className="bg-gray-800 p-4 rounded-2xl">No ongoing match. Start a new one.</div>
    )

    const battingKey = match.batting === 'A' ? 'scoreA' : 'scoreB'
    const score = match[battingKey]
    const oversStr = formatOvers(score.balls)

    const m = state.match
    const side = m.batting
    const sc = side === 'A' ? m.scoreA : m.scoreB
    const target = m.target ?? 0
    const runsNeeded = target ? Math.max(0, target - sc.runs) : null
    const ballsRemaining = target ? (m.oversLimit * 6 - sc.balls) : null

    const crr = sc.balls > 0 ? (sc.runs / (sc.balls / 6)).toFixed(2) : '0.00'
    const rrr = target && ballsRemaining > 0
        ? (runsNeeded / (ballsRemaining / 6)).toFixed(2)
        : null

    const bowlingSide = side === 'A' ? 'B' : 'A'
    const bowler = m.players[bowlingSide][m.current.bowlerIndex]
    const oversBowled = (bowler.overs || 0) + (bowler.balls || 0) / 6
    const economy = oversBowled > 0 ? (bowler.runsConceded / oversBowled).toFixed(2) : '0.00'


    const addBall = (runs = 0, extra = false, extraType = null, wicket = false) => {
        updateMatch(match => {
            if (!match || match.finished) return // no-op
            // compute current over BEFORE applying ball (for history)
            const battingSide = match.batting
            const sc = battingSide === 'A' ? match.scoreA : match.scoreB
            const overNum = Math.floor(sc.balls / 6) + 1

            // apply ball (mutates match)
            applyBall(match, runs, extra, extraType, wicket)

            // push compact canonical history entry
            match.history = match.history || []
            match.history.push({
                type: 'ball',
                runs,             // runs param (batsman running if applicable)
                extra: !!extra,
                extraType: extraType || null,
                wicket: !!wicket,
                over: overNum,
                // optional fields to help fast summary without recalculation
                totalRuns: (extra && extraType === 'wide') ? (1 + runs) : (extra && extraType === 'noball' ? 1 + runs : (extra ? runs : runs)),
                batsmanRuns: (extra && extraType === 'noball') ? runs : (extra ? 0 : runs)
            })

            // keep history bounded (context also caps but defensive here)
            if (match.history.length > 2000) match.history.shift()
        })
    }

    const undo = () => {
        updateMatch(prev => {
            if (!prev?.history?.length) return prev
            const m = structuredClone(prev)
            m.history.pop()
            return recalculateMatch(m)
        })
    }




    const finishMatch = () => updateMatch(prev => ({ ...prev, finished: true }))

    const gotoSummary = () => navigate('/summary')




    return (
        <div className="space-y-4">
            <div className="bg-dark-800 p-5 rounded-2xl shadow-lg border border-dark-700">
                <div className="flex justify-between items-center">
                    <div>
                        <div className="text-sm text-gray-400">
                            {match.inning === 1 ? '1st Innings' : match.finished ? 'Match Ended' : '2nd Innings'}
                        </div>
                        <div className="text-2xl font-bold text-accent">
                            {match.batting === 'A' ? match.teamA.name : match.teamB.name}
                        </div>
                    </div>

                    <div className="text-right">
                        <div className="text-4xl font-extrabold text-white tracking-wide">
                            {score.runs}/{score.wickets}
                        </div>
                        <div className="text-sm text-gray-400">
                            Overs {oversStr} • Extras {score.extras}
                        </div>
                    </div>
                </div>

                {/* This Over */}
                <div className="mt-3 text-sm text-gray-300">
                    <span className="font-medium text-gray-400 mr-2">This Over:</span>
                    {getCurrentOverBalls(match.history || [], score.balls).map((b, i) => (
                        <span
                            key={i}
                            className={`inline-block mx-1 px-2 py-0.5 rounded-md text-sm ${b === 'W'
                                ? 'bg-red-600 text-white'
                                : b === '6'
                                    ? 'bg-green-600 text-white'
                                    : b === '4'
                                        ? 'bg-yellow-400 text-gray-900'
                                        : 'bg-dark-700 text-gray-200'
                                }`}
                        >
                            {b}
                        </span>
                    ))}
                </div>
            </div>

            <div className="bg-gray-800 p-4 rounded-2xl shadow-md">

                <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="p-2 bg-gray-900 rounded-md">
                        <div className="text-xs text-gray-400">Striker</div>
                        <div className="font-medium">{match.players[match.batting][match.current.strikerIndex]?.name}</div>
                    </div>
                    <div className="p-2 bg-gray-900 rounded-md">
                        <div className="text-xs text-gray-400">Non-striker</div>
                        <div className="font-medium">{match.players[match.batting][match.current.nonStrikerIndex]?.name}</div>
                    </div>
                    <div className="p-2 bg-gray-900 rounded-md mt-2">
                        <div className="text-xs text-gray-400">Bowler</div>
                        <div className="font-medium">
                            {match.players[match.batting === 'A' ? 'B' : 'A'][match.current.bowlerIndex]?.name}
                        </div>
                        <div className="text-xs text-gray-400">
                            {match.players[match.batting === 'A' ? 'B' : 'A'][match.current.bowlerIndex]?.overs}.{match.players[match.batting === 'A' ? 'B' : 'A'][match.current.bowlerIndex]?.balls || 0} •
                            {match.players[match.batting === 'A' ? 'B' : 'A'][match.current.bowlerIndex]?.runsConceded || 0} runs •
                            {match.players[match.batting === 'A' ? 'B' : 'A'][match.current.bowlerIndex]?.wicketsTaken || 0} wkts
                        </div>
                        <div className="text-xs text-gray-400">
                            Econ: {economy}
                        </div>

                    </div>

                </div>
                <div className="mt-3 text-sm text-gray-300">
                    <div>CRR: {crr}</div>
                    {target && !m.finished && (
                        <>
                            <div>Target: {target}</div>
                            <div>Runs Needed: {runsNeeded} from {ballsRemaining} balls</div>
                            <div>RRR: {rrr}</div>
                        </>
                    )}
                    {m.finished && (
                        <div className="text-green-400 font-semibold mt-2">
                            {m.result}
                        </div>
                    )}
                </div>


            </div>
            <div className="mt-3">
                <label className="text-sm text-gray-400">Current Bowler</label>
                <select
                    value={match.current.bowlerIndex}
                    onChange={e =>
                        updateMatch(m => {
                            m.current.bowlerIndex = Number(e.target.value)
                            return m
                        })
                    }
                    className="w-full p-2 rounded bg-gray-900 mt-1"
                >
                    {match.players[match.batting === 'A' ? 'B' : 'A'].map((p, i) => (
                        <option key={p.id} value={i}>{p.name}</option>
                    ))}
                </select>
            </div>


            {/* Scoring controls (mobile friendly) */}
            <div className="grid grid-cols-4 gap-3 mt-4">
                {[0, 1, 2, 3, 4, 5, 6].map(n => (
                    <button
                        key={n}
                        onClick={() => addBall(n)}
                        className="py-4 rounded-lg text-lg bg-dark-700 text-white font-semibold hover:bg-dark-600 transition"
                    >
                        {n}
                    </button>
                ))}
                <button onClick={() => addBall(1, true, 'wide')} className="py-4 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold">WD</button>
                <button onClick={() => addBall(0, true, 'noball')} className="py-4 rounded-lg bg-red-500 hover:bg-red-400 text-white font-semibold">NB</button>
                <button onClick={() => addBall(0, true, 'bye')} className="py-4 rounded-lg bg-gray-600 text-white font-semibold">B</button>
                <button onClick={() => addBall(0, true, 'legbye')} className="py-4 rounded-lg bg-gray-600 text-white font-semibold">LB</button>
                <button onClick={() => addBall(0, false, null, true)} className="py-4 rounded-lg bg-danger text-white font-semibold">W</button>
            </div>

            <div className="mt-4 flex gap-3">
                <button onClick={undo} className="flex-1 py-3 bg-dark-700 text-gray-200 rounded-lg hover:bg-dark-600 transition">Undo</button>
                <button onClick={finishMatch} className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-500 transition">Finish</button>
                <button onClick={gotoSummary} className="flex-1 py-3 bg-accent text-gray-900 rounded-lg font-semibold hover:bg-emerald-500 transition">Summary</button>
            </div>


            <section className="bg-gray-800 p-3 rounded-2xl shadow-md">
                <h4 className="text-sm text-gray-400">Players</h4>
                <div className="mt-2 grid grid-cols-2 gap-2">
                    {match.players[match.batting].map((p, i) => (
                        <div key={p.id} className={`p-2 rounded-md ${p.onStrike ? 'ring-2 ring-accent' : 'bg-gray-900'}`}>
                            <div className="text-sm font-medium">{p.name}</div>
                            <div className="text-xs text-gray-400">
                                {p.runs}({p.balls}) • SR {p.balls > 0 ? ((p.runs / p.balls) * 100).toFixed(1) : 0}
                                • 4s {p.fours} • 6s {p.sixes}
                                {p.isOut ? ' • OUT' : ''}
                            </div>

                        </div>
                    ))}
                </div>
            </section>

        </div>
    )
}